import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC } from 'react';

import { useI18n } from '@catawiki/cata-style';

import {
  fetchExpert as fetchRemoteExpert,
  RemoteExpert,
} from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { EXPERT_STUB } from '@/shared/stubs/experts';
import { getBuilderString } from '@/shared/text';
import { SharedBuilderComponent } from '@/shared/types';
import { nullableData } from '@/shared/utils/http';
import { getSpan, TRACER_NAME_BUILDER } from '@/shared/utils/tracer';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import { Expert, Row } from './Row';

type ExpertInput = {
  expertId: number;
  title?: string;
  subtitle?: string;
};

interface BuilderInput extends SharedBuilderComponent {
  title?: string;
  description?: string;
  expertsInput?: ExpertInput[];

  action: BaseAction;
  actionVariant: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
  experts?: Expert[];
};

export const ListExperts: FC<Props> = ({
  title,
  description,
  experts = [],
  expertsInput = [],
  action,
  actionVariant,

  locale,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
}) => {
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  const blockLocale = locale || builderState?.context.locale || 'en';
  const i18n = useI18n();

  const expertsOrStubs =
    experts.length > 0
      ? experts.filter((expert) => expert.mainImage)
      : Array(expertsInput.length).fill(EXPERT_STUB);

  const expertsWithExpertise = expertsOrStubs.map((expert) => {
    const expertise = i18n.t(
      expert.gender === 'Male'
        ? 'els.review.expert_in'
        : 'els.review.expert_in_female',
      { category: expert.expertise }
    );

    return { ...expert, expertise: expert.expertiseInput || expertise };
  });

  return (
    <section
      {...attributes}
      data-testid="list-experts"
      className={cn(backgroundColorClassName, spacingStyles)}
    >
      <ComponentHeader
        title={title}
        description={description}
        locale={blockLocale}
        action={action}
        actionVariant={actionVariant}
        className={'u-wrapper'}
        headerClassName={cn({
          'u-m-b-xl u-m-b-lg-xxl-6': expertsWithExpertise.length > 0,
        })}
      >
        <Row experts={expertsWithExpertise} />
      </ComponentHeader>
    </section>
  );
};

async function fetchExpert(expertId: number, locale: string) {
  const eitherExperts = await fetchRemoteExpert(expertId, locale);
  const expert = pipe(eitherExperts, nullableData) || ({} as RemoteExpert);
  return expert;
}

export function dataFetcher(locale: string) {
  return async ({ expertsInput = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'ListExperts.dataFetcher');
    if (expertsInput.length === 0) return [];

    const remoteExperts = await Promise.all(
      expertsInput.map((input) => fetchExpert(input.expertId, locale))
    );

    const expertsInputById: { [k: number]: ExpertInput } = expertsInput.reduce(
      (acc, value) => ({ ...acc, [value.expertId]: value }),
      {}
    );

    const experts = remoteExperts.map((remoteExpert) => {
      const input = expertsInputById[remoteExpert.id];

      return {
        mainImage: remoteExpert.main_image || '',
        url: remoteExpert.url || '',
        name: getBuilderString(input?.title, locale) || remoteExpert.name || '',
        expertiseInput: getBuilderString(input?.subtitle, locale) || '',
        expertise: remoteExpert.expertise,
      };
    });

    span.end();
    return {
      locale,
      experts,
    };
  };
}

export const NAME = 'ListExperts';

export function componentRegister() {
  Builder.registerComponent(ListExperts, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'expertsInput',
        type: 'list',
        friendlyName: 'Experts',
        subFields: [
          {
            name: 'expertId',
            friendlyName: 'Expert ID',
            type: 'number',
          },
          {
            name: 'title',
            friendlyName: 'Title',
            type: 'CatawikiPlainEditor',
          },
          {
            name: 'subtitle',
            friendlyName: 'Subtitle',
            type: 'CatawikiPlainEditor',
          },
        ],
      },
    ],
    noWrap: true,
  });
  Builder.registerComponent(ListExperts, {
    name: `v3:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'expertsInput',
        type: 'list',
        friendlyName: 'Experts',
        subFields: [
          {
            name: 'expertId',
            friendlyName: 'Expert ID',
            type: 'number',
          },
          {
            name: 'title',
            friendlyName: 'Title',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
          {
            name: 'subtitle',
            friendlyName: 'Subtitle',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
        ],
      },
    ],
    noWrap: true,
  });
  return `v2:${NAME}`;
}

export const componentDataFetchers = (locale: string) => ({
  [`v2:${NAME}`]: dataFetcher(locale),
  [`v3:${NAME}`]: dataFetcher(locale),
});
